///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Local thumbnail cache for saved views (IndexedDB, keyed by view id).
/// Thumbnails are a device-local cache and are never synced: a device
/// that didn't capture the view shows a placeholder until the user flies
/// there again.
///
/// Capture is two-phase because the WebGL context has no
/// preserveDrawingBuffer: React code calls requestCapture(viewId), and the
/// engine's post-render hook calls capturePending(canvas) while the frame's
/// drawing buffer is still valid.
///

const cDbName = "EarthBrowserViewThumbnails"
const cDbVersion = 1
const cStoreName = "thumbnails"
const cThumbnailWidth = 256
const cJpegQuality = 0.7

interface ThumbnailRecord {
	id: string
	blob: Blob
	capturedAt: number
}

export class ViewThumbnailStore {
	public static readonly shared = new ViewThumbnailStore()

	private db: IDBDatabase | null = null
	private opening?: Promise<IDBDatabase>
	private pending: string[] = []

	/** Queue a thumbnail capture for the next rendered frame */
	public requestCapture(viewId: string): void {
		if (!this.pending.includes(viewId)) {
			this.pending.push(viewId)
		}
	}

	/**
	 * Called from the engine's post-render hook. Copies the drawing buffer
	 * synchronously (it is only valid inside the current rAF callback),
	 * then encodes and stores asynchronously.
	 */
	public capturePending(canvas: HTMLCanvasElement): void {
		if (this.pending.length === 0) return
		if (canvas.width === 0 || canvas.height === 0) return

		const ids = this.pending
		this.pending = []

		const width = cThumbnailWidth
		const height = Math.max(1, Math.round(width * canvas.height / canvas.width))
		const scaled = document.createElement("canvas")
		scaled.width = width
		scaled.height = height
		const ctx = scaled.getContext("2d")
		if (!ctx) return

		// Synchronous copy while the WebGL buffer is valid
		ctx.drawImage(canvas, 0, 0, width, height)

		scaled.toBlob(blob => {
			if (!blob) return
			for (const id of ids) {
				void this.store(id, blob)
			}
		}, "image/jpeg", cJpegQuality)
	}

	public async getThumbnail(viewId: string): Promise<Blob | null> {
		const db = await this.open()
		return new Promise((resolve, reject) => {
			const request = db.transaction(cStoreName, "readonly")
				.objectStore(cStoreName)
				.get(viewId)
			request.onsuccess = () => resolve((request.result as ThumbnailRecord | undefined)?.blob ?? null)
			request.onerror = () => reject(request.error ?? new Error("IndexedDB get failed"))
		})
	}

	public async deleteThumbnail(viewId: string): Promise<void> {
		const db = await this.open()
		return new Promise((resolve, reject) => {
			const request = db.transaction(cStoreName, "readwrite")
				.objectStore(cStoreName)
				.delete(viewId)
			request.onsuccess = () => resolve()
			request.onerror = () => reject(request.error ?? new Error("IndexedDB delete failed"))
		})
	}

	private async store(viewId: string, blob: Blob): Promise<void> {
		try {
			const db = await this.open()
			const record: ThumbnailRecord = { id: viewId, blob, capturedAt: Date.now() }
			await new Promise<void>((resolve, reject) => {
				const request = db.transaction(cStoreName, "readwrite")
					.objectStore(cStoreName)
					.put(record)
				request.onsuccess = () => resolve()
				request.onerror = () => reject(request.error ?? new Error("IndexedDB put failed"))
			})
		} catch (error) {
			console.warn("[ViewThumbnailStore] Failed to store thumbnail:", error)
		}
	}

	private open(): Promise<IDBDatabase> {
		if (this.db) return Promise.resolve(this.db)
		this.opening ??= new Promise((resolve, reject) => {
			const request = indexedDB.open(cDbName, cDbVersion)
			request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"))
			request.onsuccess = () => {
				this.db = request.result
				resolve(request.result)
			}
			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result
				if (!db.objectStoreNames.contains(cStoreName)) {
					db.createObjectStore(cStoreName, { keyPath: "id" })
				}
			}
		})
		return this.opening
	}
}
